/**
 * <MarketBrief> — AI 시황 정리 (제목 + 본문 2~3줄 목록형)
 *
 * 레이아웃:
 *   [01] 연준 금리 동결·파월 매파 발언
 *        • 4월 FOMC 기준금리 5.25% 유지, 2명 dissent
 *        • 파월 "인플레 둔화 확인 전 인하 없음"
 *        • 6월 인하 확률 38%→22%로 하락
 *   [02] 엔비디아 실적 beat·시간외 8% 급등
 *        • Q1 매출 260억 달러 (컨센서스 245억)
 *        • 데이터센터 매출 전년동기비 427% 증가
 *   ...
 *
 * 데이터: GET /api/ticker/indices/explanation (marketBrief 필드 소비)
 *   marketBrief: Array<{ title: string, body: string[] }>
 *
 * indices 데이터는 <IndexPulse> 위젯이 담당 (같은 엔드포인트지만 소비 필드 다름)
 */
"use client";

import { useEffect, useRef, useState } from "react";
import type { WidgetMeta, WidgetProps } from "../types";

interface BriefItem {
  title: string;
  body: string[];
  imageUrl?: string | null;     // 사용자 결정 #8: backend 가 매칭한 Polygon news image
  imageSource?: string | null;  // image source (publisher name)
}

// TOPIC_THEMES / getThumbTheme 제거 — 그라디언트 fallback 폐기 (#1).
// 이미지 없는 글은 thumb 자체 안 띄움 (자리도 차지 X).
interface ExplanationData {
  updatedAt: string;
  marketBrief?: BriefItem[];
}

const REFRESH_MS = 5 * 60 * 1000; // 5분 (서버 캐시는 1시간, 프론트는 더 짧게)

export function MarketBrief(_props: WidgetProps) {
  const [data, setData] = useState<ExplanationData | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/indices/explanation", { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = (await res.json()) as ExplanationData;
        if (mountedRef.current) {
          setData(d);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) setLoading(false);
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(t);
    };
  }, []);

  const rawItems = data?.marketBrief ?? [];
  // 구버전 string[] 스키마 방어
  const items: BriefItem[] = rawItems
    .filter((it): it is BriefItem => typeof it === "object" && it != null && "title" in it && Array.isArray((it as BriefItem).body))
    .slice(0, 3);

  return (
    <div className="dw-body dw-mb">
      <div className="dw-head">
        <span className="dw-title">시황 정리</span>
      </div>

      {loading && items.length === 0 ? (
        <div className="dw-mb-list">
          {[0, 1, 2].map((i) => (
            <article key={i} className="dw-mb-item">
              <div className="dw-mb-title-row">
                <span className="dw-mb-num">{String(i + 1).padStart(2, "0")}</span>
                <span className="bs-skel" style={{ width: "70%", height: 16, display: "block" }} />
              </div>
              <div className="dw-mb-body">
                <span className="bs-skel" style={{ width: "95%", height: 12, display: "block", marginBottom: 6 }} />
                <span className="bs-skel" style={{ width: "80%", height: 12, display: "block" }} />
              </div>
            </article>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="dw-mb-empty">시황 정리 준비중</div>
      ) : (
        <div className="dw-mb-list">
          {items.map((it, i) => (
            <article key={i} className="dw-mb-item dw-mb-item--with-thumb">
              <div className="dw-mb-text">
                <div className="dw-mb-title-row">
                  <span className="dw-mb-num">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="dw-mb-title">{it.title}</h3>
                </div>
                <ul className="dw-mb-body">
                  {it.body.slice(0, 3).map((line, j) => (
                    <li key={j} className="dw-mb-bullet">{line}</li>
                  ))}
                </ul>
              </div>
              {/* 진짜 사진 있을 때만 노출 — 그라디언트 fallback 제거 (사용자 결정 #1).
                  이미지 로드 실패 시 부모 자체 hide (자리 차지도 X). */}
              {it.imageUrl && (
                <div
                  className="dw-mb-thumb dw-mb-thumb--photo"
                  aria-hidden="true"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={it.imageUrl}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      const parent = (e.currentTarget.parentElement as HTMLElement | null);
                      if (parent) parent.style.display = "none";
                    }}
                  />
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {data?.updatedAt && (
        <div className="dw-mb-footer">
          {new Date(data.updatedAt).toLocaleTimeString("ko-KR", {
            hour: "2-digit",
            minute: "2-digit",
          })}{" "}
          기준
        </div>
      )}
    </div>
  );
}

export const marketBriefMeta: WidgetMeta = {
  type: "market-brief",
  name: "시황 정리",
  description: "AI — 오늘 미국장 Top 2~3 이슈 (제목 + 본문)",
  icon: "📝",
  category: "ai",
  defaultSize: { w: 12, h: 5 },
  availableSizes: [
    { w: 6, h: 5 },
    { w: 8, h: 5 },
    { w: 12, h: 5 },
    { w: 12, h: 6 },
  ],
  minW: 4,
  maxW: 12,
  minH: 4,
  maxH: 8,
  component: MarketBrief,
};
